import {
  RESTART_KINDS,
  ceremonyBaseTicks,
  simOwnsAerialArm,
  createMatchState,
  parseConfigJson,
  clonePlayer,
  pickPassTarget,
  dequantPlayer,
  dequantBall,
  dequantU16,
  dequantFacing,
  QUANT,
  encodeHello,
  encodeJsonMsg,
  encodePing,
  encodeInputFrame,
  encodeSwitchRequest,
  SWITCH_CYCLE,
  decodeJsonMsg,
  decodeWelcome,
  decodePong,
  decodeMatchEvent,
  decodeSwitchAssign,
  MSG,
  BTN,
  PROTOCOL_VERSION,
  type DecodedSnapshot,
  type EntityId,
  type MatchState,
  type ModeInfo,
  type PlayerInput,
  type RestartKind,
  type SimConfig,
  type Transport,
} from '@fafi/shared';
import { SnapshotBuffer } from '../net/snapshotBuffer';
import { Interpolator } from '../net/interpolator';
import { Predictor, type SnapEntity } from '../net/predictor';
import { ClockSync } from '../net/clockSync';
import { LeadController } from '../net/leadController';
import { Connection, type ConnState } from '../net/connection';
import { MultiplexTransport } from '../net/multiplex';
import { EventTicker } from './eventTicker';
import { passSoftLock } from './softLock';
import type { ViewState, ViewEntity } from './viewState';
import { KickIntent } from '../input/kickIntent';

// M2-S9 networked game loop: composes the tested netcode pieces (§10.2/§10.3/§10.4) over a real socket.
// Flow: Hello → CREATE_ROOM/JOIN_ROOM → (LOBBY_STATE…) → host auto-STARTs at 2 players → Welcome (cfg +
// own entity — the ONLY cfg source, §17/T-NET-016) → snapshots. Own entity: Predictor (shared stepEntity);
// others + ball: stream-anchored Interpolator; inputs: 30 Hz, lead-steered, ×3-redundant frames; drops:
// Connection resumes with the gameSessionToken (RESUME carries it hex-encoded; §12.3).

/** D141 — parse the optional pending restart off the Welcome's roomStateJson.
 *  Returns `undefined` when the server sent no `restart` key at all (a pre-D141 server, or a Welcome we
 *  should leave the current value alone for), `null` for an explicit "no restart pending", and a record
 *  only when every field validates. Anything malformed is treated as absent: this value gates the
 *  keeper-dive verb, so a wrong kind is worse than a missing one. */
function parseWelcomeRestart(
  raw: unknown,
): { kind: RestartKind; team: number; taker: number | null; spot: { x: number; y: number }; ceremony: boolean } | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const kind = r['kind'];
  const team = r['team'];
  const taker = r['taker'];
  const spot = r['spot'] as Record<string, unknown> | undefined;
  if (typeof kind !== 'string' || !RESTART_KINDS.includes(kind as RestartKind)) return undefined;
  if (team !== 0 && team !== 1) return undefined;
  if (taker !== null && !Number.isInteger(taker)) return undefined;
  if (!spot || typeof spot['x'] !== 'number' || typeof spot['y'] !== 'number' || !Number.isFinite(spot['x']) || !Number.isFinite(spot['y'])) return undefined;
  return { kind: kind as RestartKind, team, taker: taker as number | null, spot: { x: spot['x'], y: spot['y'] }, ceremony: r['ceremony'] === true };
}

const PING_INTERVAL_MS = 500;
const FULL_SENTINEL = 0xffffffff;
const MAX_CATCHUP_TICKS = 10; // stamp at most this many input ticks per frame (background-tab recovery)
const RE_ANCHOR_GAP_TICKS = 300; // beyond this (≈10 s) never step-simulate — drop + re-seed from a snapshot
const NO_POSSESSOR = 255; // wire sentinel (§9.3)
const BALL_BLEND_MS = 150; // pop-free blend when the ball switches timelines (carried ↔ interpolated)
const KICK_DETACH_MAX_MS = 600; // safety: restore the glue if the server never releases (kick out of reach)
const RESUME_WATCHDOG_MS = 5000; // D45 Item 1: per-stage timeout before falling back / giving up on a resume
/** D62: a PLAYING client with no applied snapshot for this long has a dead stream, whatever the socket
 *  says. ~3 s ≈ 45 missed snapshots at the 15 Hz send rate — far outside jitter, well inside patience. */
export const STREAM_DEAD_MS = 3000;
/** D62: hard ceiling on the D51 own-ball present-timeline extrapolation (≈ 4 snapshot intervals). */
const BALL_EXTRAP_MAX_SEC = 0.25;

export type JoinIntent = { kind: 'create'; teamSize: number } | { kind: 'join'; code: string };

export interface NetGameOpts {
  /** Opens a fresh underlying socket (fakeLag-wrapped upstream when requested). */
  makeSocket: () => Transport;
  join: JoinIntent;
  /** Sampled once per stamped input tick. */
  sampleInput: () => PlayerInput;
  /** Room code became known (create path updates the URL for sharing). */
  onCode?: (code: string) => void;
  /** Fired exactly once per (tick, idx) event — banners/SFX hook (D18). D119: the server's own
   *  (tick, idx) key rides along. It was already on the wire and thrown away here, so the debug log
   *  had to stamp `performance.now()` instead — a DELIVERY time, which cannot resolve two sim ticks
   *  that arrived in one packet. Every ordering question about an incident needs the sim tick. */
  onEvent?: (e: import('@fafi/shared').SimEvent, tick: number, idxWithinTick: number) => void;
  /** D35: the controlled entity changed (auto/manual switch). isGk/posX from the newest snapshot. */
  onControlledChange?: (info: { entityId: number; isGk: boolean; posX: number }) => void;
  /** D43: display name sent in the Hello (server-moderated; shown over the head in net play). */
  playerName?: string;
  /** D41: rematch vote tally (votes / needed) — drives the full-time screen counter. */
  onRematchState?: (votes: number, needed: number) => void;
  /** D45 Item 1: the resume/join watchdog gave up (no snapshots, no lobby) — show an error screen. */
  onFatal?: (msg: string) => void;
  now?: () => number;
  /** Account credential (§12.4) — attributes the session to its account for stats + display name. */
  authToken?: string;
  /** Play as this club team (create path) — links the match to the team for its record (M9-C). */
  clubTeamId?: string;
}

interface RingEntry {
  t: number;
  input: PlayerInput;
}

const flagBit = (flags: number, bit: number): boolean => (flags & bit) !== 0;
const cardOf = (flags: number): ViewEntity['card'] => {
  const c = (flags >> 4) & 3;
  return c === 0 ? null : c === 1 ? 'YELLOW' : 'RED';
};
const hex = (b: Uint8Array): string => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

export class NetGame {
  private readonly now: () => number;
  private readonly mux: MultiplexTransport;
  private connection: Connection | null = null;
  private snaps = new SnapshotBuffer(); // D62: re-seeded on a stream-death resume (baselines are void)
  private readonly interp = new Interpolator();
  private readonly clock = new ClockSync();
  private readonly lead = new LeadController();
  private predictor: Predictor | null = null;

  private cfg: SimConfig | null = null;
  private mode: ModeInfo | null = null;
  private scratch: MatchState | null = null;
  private ownId = -1;
  private mySlot = -1; // this session's slot — SwitchAssigns for it move my controlled entity (§8.2)
  private matchPhase = 'KICKOFF'; // D41: authoritative match phase, tracked from PHASE/WHISTLE events
  /** D137/D130 — the pending restart, tracked off the D122 wire fields and cleared when play resumes.
   *  `ceremony` flips on WHISTLE{RESTART}, which is exact: the server emits it on the single tick the
   *  ceremony budget runs out, so the predictor's `inCeremony` needs no arithmetic of its own. */
  private currentRestart: { kind: import('@fafi/shared').RestartKind; team: number; taker: number | null; spot: { x: number; y: number }; ceremony: boolean } | null = null;
  private slotNames: string[] = []; // D43: session display names by slot (LOBBY_STATE)
  private readonly slotEntities = new Map<number, number>(); // D43: EVERY session's controlled entity (all SwitchAssigns)
  private gkMaskUntilTick = 0; // D36 keeper-switch safety: mask own-goal-ward move input until this tick
  private gkMaskSignX = 0; // x-sign toward the own goal while the mask is armed (0 = unarmed)
  private snapCount = 0; // snapshots applied — E2E readiness signal (T-E2E-001)
  private matchId = ''; // D62: the match EPOCH from the Welcome — distinguishes a rematch from a resume
  private clockTicks = 0; // D62: the AUTHORITATIVE match clock, straight off the snapshot header
  private lastSnapAtMs = -1; // D62: wall time of the last APPLIED snapshot — the stream-liveness heartbeat
  private streamDead = false; // D62: the stream stopped while the socket lived (a frozen world, honestly labelled)
  private readonly predErr: number[] = []; // ring of recent prediction-error magnitudes (m) — T-E2E-003
  private code = '';
  private tokenHex = '';
  private players = 0;
  private teamSize = 5;
  private teams: [number, number] = [0, 0];
  private voice = true; // lobby proximity-voice flag (P14)
  private difficulty = 3; // D46 Items 3+4: lobby AI difficulty (echoed from LOBBY_STATE for display)
  private isHost = false;
  private yourTeam = 0;
  private matchStarted = false;
  private startSent = false;
  private lastPingMs = -Infinity;
  private lastStamped = -1;
  private readonly ring: RingEntry[] = [];
  readonly ticker = new EventTicker();
  private readonly score: [number, number] = [0, 0];
  lastError = '';

  private joinFallbackSent = false;
  // D45 Item 1: resume/join watchdog — a reload into a dead room used to freeze forever waiting on snapshots.
  private awaitingConnect = false; // watching the join/resume path for liveness (cleared on first progress)
  private sentResume = false; // did we open with RESUME (stored token) vs a fresh JOIN
  private watchStart = -1; // wall time the current watchdog stage began (stamped on first pump)
  private watchStage = 0; // 0 = awaiting first response · 1 = awaiting the JOIN fallback · 2 = fatal (done)

  constructor(private readonly opts: NetGameOpts) {
    this.now = opts.now ?? ((): number => performance.now());
    this.mux = new MultiplexTransport(opts.makeSocket());
    this.mux.leg('game').onMessage((b) => this.onBytes(b));
    this.hello();
    if (opts.join.kind === 'join') {
      this.code = opts.join.code;
      this.awaitingConnect = true; // D45: watch this path for liveness (snapshots or a lobby)
      // reload-resume (§12.3): a mid-match F5 re-enters via RESUME with the stored token (a plain
      // re-JOIN would bounce off ALREADY_STARTED); BAD_RESUME falls back to a fresh JOIN below.
      const stored = this.storedResume();
      if (stored && stored.code === opts.join.code) {
        this.tokenHex = stored.token;
        this.sentResume = true;
        this.mux.send(encodeJsonMsg({ t: 'RESUME', code: stored.code, token: stored.token }));
      } else {
        this.mux.send(encodeJsonMsg({ t: 'JOIN_ROOM', code: opts.join.code, ...(opts.authToken ? { authToken: opts.authToken } : {}) }));
      }
    } else {
      this.isHost = true; // the creator IS the host — show host controls without waiting on the first LOBBY_STATE
      this.mux.send(
        encodeJsonMsg({
          t: 'CREATE_ROOM',
          // D46: difficulty is NOT set at create time — the host picks it in the lobby (SET_MODE.difficulty).
          mode: { teamSize: opts.join.teamSize },
          ...(opts.authToken ? { authToken: opts.authToken } : {}),
          ...(opts.clubTeamId ? { clubTeamId: opts.clubTeamId } : {}),
        }),
      );
    }
  }

  private storedResume(): { code: string; token: string } | null {
    try {
      const raw = sessionStorage.getItem('fafi.resume');
      if (!raw) return null;
      const p = JSON.parse(raw) as { code?: unknown; token?: unknown };
      return typeof p.code === 'string' && typeof p.token === 'string' ? { code: p.code, token: p.token } : null;
    } catch {
      return null; // storage unavailable (private mode) ⇒ ephemeral session (§13.2 storage wrapper lands M7)
    }
  }

  private hello(): void {
    this.mux.send(
      encodeHello({ ver: PROTOCOL_VERSION, flags: 0, gameSessionToken: new Uint8Array(16), guestId: new Uint8Array(16), name: this.opts.playerName ?? '' }),
    );
  }

  /** D43: name tags — every OTHER human's current entity + display name (from LOBBY_STATE + SwitchAssigns). */
  humanTags(): Array<{ entityId: number; name: string }> {
    const out: Array<{ entityId: number; name: string }> = [];
    for (const [slot, entityId] of this.slotEntities) {
      if (slot === this.mySlot) continue;
      out.push({ entityId, name: this.slotNames[slot] || `P${slot + 1}` });
    }
    return out;
  }

  /** Resume path (§12.3): fresh socket + Hello + RESUME(code, tokenHex); the mux keeps all legs wired. */
  private makeResumeTransport(): Transport {
    this.mux.swapUnderlying(this.opts.makeSocket());
    this.hello();
    this.mux.send(encodeJsonMsg({ t: 'RESUME', code: this.code, token: this.tokenHex }));
    return this.mux.leg('conn');
  }

  // ── inbound ────────────────────────────────────────────────────────────────
  private onBytes(bytes: Uint8Array): void {
    if (bytes.length === 0) return;
    switch (bytes[0]) {
      case MSG.WELCOME:
        return this.onWelcome(bytes);
      case MSG.JSON:
        return this.onJson(bytes);
      case MSG.SNAPSHOT:
        return this.onSnapshot(bytes);
      case MSG.PONG: {
        const p = decodePong(bytes);
        this.clock.onPong(p.clientTimeMs, p.serverTimeMs, this.now(), p.serverTick); // tick anchor (§10.4)
        this.lead.onRtt(this.clock.rttMs);
        return;
      }
      case MSG.MATCH_EVENT: {
        const m = decodeMatchEvent(bytes);
        if (!this.ticker.ingest(m.tick, m.idxWithinTick, m.event)) return; // exactly-once by (tick, idx) — §4.4
        if (m.event.t === 'GOAL') this.score[m.event.team] += 1; // score rides GOAL events (§9.2, §6.2)
        if (m.event.t === 'PHASE') this.matchPhase = m.event.to; // D41: scoreboard tag + full-time screen
        // D137 — the live restart, tracked off the D122 wire fields. The client input layer needs the
        // KIND to know whether Q/E are the keeper's dives this moment; D130 reads the same record for the
        // taker's goal-lock, the camera seed and the prediction scratch.
        if (m.event.t === 'OUT_OF_PLAY') this.currentRestart = { kind: m.event.restart, team: m.event.team, taker: m.event.taker, spot: { ...m.event.spot }, ceremony: true };
        if (m.event.t === 'WHISTLE' && m.event.kind === 'RESTART' && this.currentRestart) this.currentRestart = { ...this.currentRestart, ceremony: false };
        if (m.event.t === 'PHASE' && m.event.to === 'PLAY') this.currentRestart = null;
        this.opts.onEvent?.(m.event, m.tick, m.idxWithinTick);
        return;
      }
      case MSG.SWITCH_ASSIGN: {
        // auto-switch (§8.2): the server moved a session's controlled entity. If it's MINE, follow it —
        // re-anchor prediction (+ camera + input target) to the new entity so a teammate never "ghost-moves".
        const sa = decodeSwitchAssign(bytes);
        this.slotEntities.set(sa.sessionSlot, sa.entityId); // D43: name tags track every human's entity
        if (sa.sessionSlot === this.mySlot && sa.entityId !== this.ownId) {
          this.ownId = sa.entityId;
          this.predictor = null; // rebuild from the next snapshot for the new entity (§13.2 rebase)
          // D134: there is no handover window left. The server hands the entity over on `effectiveTick`
          // and the human's input drives it from that tick, so the predictor rebuilds immediately. A
          // hostile or stale server sending a NONZERO handover byte is simply ignored — the byte is
          // frozen at 0 by contract (PT4-P10) and the client no longer has machinery to honour it.
          const snap = this.snaps.newest();
          const ps = snap?.players.find((pp) => pp.id === sa.entityId);
          if (ps) {
            const pv = dequantPlayer(ps);
            const isGk = (pv.flags & 2) !== 0;
            // D36 keeper-switch safety (mirrors the server mask so prediction never diverges): for 1 s of
            // actual control, move input toward the own goal (the keeper's side at switch time) is masked.
            if (isGk && this.cfg) {
              this.gkMaskUntilTick = sa.effectiveTick + this.cfg.switching.GK_GOALWARD_MASK_TICKS; // D134: rebased off the assign
              this.gkMaskSignX = pv.pos.x < 0 ? -1 : 1;
            } else {
              this.gkMaskUntilTick = 0;
              this.gkMaskSignX = 0;
            }
            this.opts.onControlledChange?.({ entityId: sa.entityId, isGk, posX: pv.pos.x });
          }
        }
        return;
      }
      default:
        return;
    }
  }

  private welcomeCount = 0;

  /** D46 Item 6: a NEW match epoch (rematch). The old client used to FREEZE here — the EventTicker deduped
   *  the fresh match's (tick, idx) keys against the finished match's (no PHASE ⇒ end screen stuck at
   *  FULLTIME) and the netcode clock/buffers were anchored to the old tick timeline (currentServerTick
   *  ~thousands ahead ⇒ predictor dropped every frame, nothing stamped). Wipe every per-match epoch field;
   *  keep the socket, slot, token, and names (those survive the rematch by design). */
  private resetMatchEpoch(): void {
    this.ticker.reset();
    this.snaps.reset();
    this.interp.reset();
    this.clock.reset(); // next Pong's serverTick re-anchors the tick↔wall mapping
    this.lead.reset();
    this.predictor = null;
    this.lastStamped = -1;
    this.snapCount = 0;
    this.gkMaskUntilTick = 0;
    this.gkMaskSignX = 0;
    this.kickDetachAtMs = -1;
    this.ballOwnUntilMs = -1;
    this.kickIntent.reset();
    this.prevSampledButtons = 0;
    this.ballPrev = null;
    this.ballBlend = null;
    this.currentRestart = null; // D130: a rematch epoch must not inherit the previous match's restart
    this.lastBallCarried = false;
    this.lastBallPossessor = -1;
    this.ring.length = 0;
    this.predErr.length = 0;
    this.slotEntities.clear(); // the new match re-broadcasts every SwitchAssign
    this.lastPingMs = -Infinity; // ping now → re-anchor the clock ASAP on the fresh timeline
  }

  private onWelcome(bytes: Uint8Array): void {
    // D41: a Welcome also arrives when a REMATCH starts — reset the per-match presentation state.
    // D46 Item 6: on the SECOND+ Welcome (a rematch — first-connect already starts clean), do the FULL
    // epoch reset so the client doesn't freeze on the finished match's stale ticker/clock/buffers.
    this.welcomeCount += 1;
    const w = decodeWelcome(bytes);
    const json = JSON.parse(w.roomStateJson) as {
      mode: ModeInfo;
      simConfig: unknown;
      matchId?: string;
      yourEntityId?: number;
      yourSlot?: number;
      score?: [number, number];
      restart?: unknown;
    };
    // D62: a Welcome arrives for a REMATCH *and* for every mid-match RESUME. Only a rematch is a new
    // epoch. Keying on welcomeCount treated a reconnect as a brand-new match: it wiped the interpolator,
    // the snapshot buffer, the clock and the ticker, and slammed the HUD phase to KICKOFF mid-half —
    // which is the bogus 'PHASE → KICKOFF' the freeze log recorded 0.5 s after the drop. The matchId is
    // the epoch: a rematch mints a new one, a resume carries the same one.
    const epoch = json.matchId ?? '';
    const newEpoch = epoch !== '' && this.matchId !== '' && epoch !== this.matchId;
    if (newEpoch || (epoch === '' && this.welcomeCount > 1)) {
      this.resetMatchEpoch(); // genuine rematch (or a pre-D62 server that cannot tell us)
      this.score[0] = 0;
      this.score[1] = 0;
      this.matchPhase = 'KICKOFF';
    }
    this.matchId = epoch;
    this.cfg = parseConfigJson(JSON.stringify(json.simConfig)); // the wire-delivered frozen cfg (§17)
    this.mode = json.mode;
    if (typeof json.yourEntityId === 'number') this.ownId = json.yourEntityId;
    if (typeof json.yourSlot === 'number') this.mySlot = json.yourSlot;
    if (Array.isArray(json.score)) {
      this.score[0] = json.score[0]; // seed the HUD from authoritative score (events aren't replayed on resume)
      this.score[1] = json.score[1];
    }
    // D141: …and the PENDING RESTART, for the same reason. `currentRestart` is otherwise written only by
    // OUT_OF_PLAY, and the wire snapshot has no restart field, so a keeper who reloaded mid-penalty spent
    // the rest of the window with `hud.defendingPenalty` false — his dive keys gated off, and the E
    // binding falling through to its other meaning (TACKLE) because the taker owns the ball. Validated
    // rather than trusted: a Welcome is server-authored, but this is the one place a JSON blob reaches
    // a gameplay gate, and an unknown kind must not light up a keeper-only verb.
    const seeded = parseWelcomeRestart(json.restart);
    if (seeded !== undefined) this.currentRestart = seeded;
    this.scratch = createMatchState(this.mode, 0); // supplies mode/conditions to stepEntity (§10.2)
    this.tokenHex = hex(w.gameSessionToken);
    try {
      sessionStorage.setItem('fafi.resume', JSON.stringify({ code: this.code, token: this.tokenHex })); // reload-resume
    } catch {
      /* storage unavailable ⇒ ephemeral session */
    }
    if (!this.connection && this.ownId >= 0) {
      this.connection = new Connection(this.mux.leg('conn'), this.ownId, () => this.makeResumeTransport(), this.now);
    }
  }

  private onJson(bytes: Uint8Array): void {
    let msg: { t: string; [k: string]: unknown };
    try {
      msg = decodeJsonMsg(bytes);
    } catch {
      return;
    }
    if (msg.t === 'LOBBY_STATE') {
      if (typeof msg['code'] === 'string') this.code = msg['code'];
      if (typeof msg['players'] === 'number') this.players = msg['players'];
      if (typeof msg['teamSize'] === 'number') this.teamSize = msg['teamSize'];
      if (Array.isArray(msg['teams'])) this.teams = [Number(msg['teams'][0] ?? 0), Number(msg['teams'][1] ?? 0)];
      if (Array.isArray(msg['names'])) this.slotNames = (msg['names'] as unknown[]).map((n) => String(n ?? ''));
      if (typeof msg['voice'] === 'boolean') this.voice = msg['voice'];
      if (typeof msg['difficulty'] === 'number') this.difficulty = msg['difficulty']; // D46: lobby line display
      if (typeof msg['isHost'] === 'boolean') this.isHost = msg['isHost'];
      if (typeof msg['yourTeam'] === 'number') this.yourTeam = msg['yourTeam'];
      if (msg['started'] === true) this.matchStarted = true;
      this.opts.onCode?.(this.code);
    } else if (msg.t === 'ERROR') {
      // D62: the JOIN fallback used to be gated on `join.kind === 'join'`, so the HOST — who created
      // the room, and whose join kind is 'create' — had NO recovery from a stale token at all: it sat
      // in RECONNECTING forever, spawning a socket every 500 ms. The room code is known either way.
      const fallbackCode = this.opts.join.kind === 'join' ? this.opts.join.code : this.code;
      if (msg['code'] === 'BAD_RESUME' && fallbackCode !== '' && !this.joinFallbackSent) {
        // stale stored token (new match in the same room, server restart) ⇒ fall back to a fresh JOIN
        this.joinFallbackSent = true;
        this.watchStart = -1; // restart the watchdog clock for the JOIN attempt
        this.watchStage = 1;
        this.mux.send(encodeJsonMsg({ t: 'JOIN_ROOM', code: fallbackCode }));
        return;
      }
      this.lastError = `${String(msg['code'] ?? 'ERROR')}: ${String(msg['msg'] ?? '')}`;
      if (msg['code'] === 'ROOM_CRASHED') {
        // D45 Item 1: the server tore the room down (crashed tick) — surface it instead of freezing
        try {
          sessionStorage.removeItem('fafi.resume');
        } catch {
          /* nothing to clear */
        }
        this.awaitingConnect = false;
        this.opts.onFatal?.('The match ended unexpectedly. Back to the menu to play again.');
      }
    } else if (msg.t === 'REMATCH_STATE') {
      // D41: rematch tally — and a fresh match resets it via the new Welcome below
      if (typeof msg['votes'] === 'number' && typeof msg['needed'] === 'number') this.opts.onRematchState?.(msg['votes'], msg['needed']);
    } else if (msg.t === 'VERSION_MISMATCH') {
      this.lastError = 'protocol version mismatch — refresh for the new build';
    }
  }

  private onSnapshot(bytes: Uint8Array): void {
    let snap: DecodedSnapshot;
    try {
      snap = this.snaps.ingest(bytes);
    } catch {
      return; // stale delta against a pre-resume baseline — the full snapshot follows (§9.3)
    }
    this.interp.onSnapshot(snap, this.now());
    // D62: the snapshot is the AUTHORITY for phase/clock/side. Previously phase came only from
    // reliable PHASE events (so a resume showed a stale/invented phase) and the clock was derived from
    // the local render timeline (so it kept counting over a dead stream — the freeze that looked alive).
    this.matchPhase = snap.phase;
    this.clockTicks = snap.clockTicks;
    this.lastSnapAtMs = this.now(); // stream-liveness heartbeat (D62 watchdog)
    if (this.scratch) {
      // the prediction scratch is a real world now, not a phantom: the side swap at half-time reaches
      // prediction, and kick gates (which read ball possession/distance) stop being evaluated against a
      // ball frozen at the centre spot.
      this.scratch.attackDir = [snap.attackDir0, -snap.attackDir0 as 1 | -1];
      this.scratch.phase = snap.phase;
      const b = dequantBall(snap.ball);
      this.scratch.ball.pos = { ...b.pos };
      this.scratch.ball.vel = { ...b.vel };
      this.scratch.ball.possessor = snap.ball.possessor === NO_POSSESSOR ? null : (snap.ball.possessor as EntityId);
      // D155: latch whether the SIM owns the aerial arm for this ball — read by the D53 input buffer,
      // which must stand down over a flighted ball so the sim's sticky arm (and its cancel) can work.
      if (this.cfg) this.lastBallAirborne = simOwnsAerialArm(this.scratch.ball, this.cfg);
      // D130 — the prediction scratch NEVER received `state.restart`, so the taker's kick was predicted
      // as an ordinary open-play one: the local animation and ball-detach fired on the PRESS edge while
      // the server fired on the RELEASE edge (+RTT). That is the second, independent leg of "it moves
      // with a delay" — present even on free kicks that worked. `phaseTicksLeft` is set so the
      // predictor's `inCeremony` matches the server's without re-deriving the countdown.
      const r = this.currentRestart;
      this.scratch.restart = r && snap.phase !== 'PLAY' ? { kind: r.kind, spot: { ...r.spot }, team: r.team as 0 | 1, taker: r.taker as EntityId | null } : null;
      // D155: the ceremony base is PHASE-DEPENDENT (KICKOFF_TICKS 90 vs RESTART_MAX_TICKS 300) and this
      // copy was not — so once D141 began seeding a `kind: 'KICKOFF'` restart out of the Welcome, the
      // scratch read `300 > 90` and `inCeremony` was true for the WHOLE kickoff window. Every 11v11 match
      // then started with the taker's own pass swallowed locally (`restartKickRefused`) and the ball
      // moving only when the server's correction arrived: exactly the "it moves with a delay" symptom
      // these three lines were added to remove. Shared with `applyRules` and `inCeremony` now, so the
      // client cannot drift from the server again.
      if (this.scratch.restart && this.cfg) {
        const base = ceremonyBaseTicks(snap.phase, this.cfg) ?? this.cfg.rules.RESTART_MAX_TICKS;
        this.scratch.phaseTicksLeft = base + (r && r.ceremony ? 1 : 0);
      }
    }
    // lead steering (§10.2): margin = ackedInputTick − tick; skip the pre-first-input sentinel value
    if (snap.ackedInputTick < FULL_SENTINEL - 1_000_000) this.lead.onMargin(snap.ackedInputTick - snap.tick);

    const ownSnap = snap.players.find((p) => p.id === this.ownId);
    if (!ownSnap || !this.cfg || !this.scratch) return;
    const pv = dequantPlayer(ownSnap);
    const snapEntity: SnapEntity = {
      pos: pv.pos,
      vel: pv.vel,
      facing: pv.facing,
      z: pv.z,
      stamina: pv.stamina,
      action: pv.action,
      actionTicks: pv.actionTicks,
    };
    if (!this.predictor || snap.tick > this.predictor.predictedTick) {
      // first snapshot, or a resume gap the predictor can't rebase across (§10.2) — re-anchor here
      const base = clonePlayer(this.scratch.players[this.ownId]!);
      base.pos = { ...pv.pos };
      base.vel = { ...pv.vel };
      base.facing = pv.facing;
      base.z = pv.z;
      base.stamina = pv.stamina;
      base.action = pv.action;
      base.actionTicks = pv.actionTicks;
      this.predictor = new Predictor(this.scratch, this.ownId, snap.tick, base);
      this.lastStamped = snap.tick;
    } else {
      this.predictor.onSnapshot(snap.tick, snapEntity, this.cfg);
      this.predErr.push(this.predictor.correctionMagnitude()); // reconciliation error this snapshot (T-E2E-003)
      if (this.predErr.length > 120) this.predErr.shift();
    }
    this.snapCount += 1;
  }

  // ── E2E / overlay introspection (window.fafi, §16.3) ──
  /** Snapshots applied since connect (T-E2E-001 readiness). */
  get snapshotsApplied(): number {
    return this.snapCount;
  }
  /** The entity this client controls (T-E2E-002 same-entity marker). */
  get controlledEntity(): number {
    return this.ownId;
  }
  /** D130: the live restart (kind/team/taker/spot), or null in open play. Read by the camera seed and
   *  by the taker's goal-aim gate. */
  get restart(): { readonly kind: import('@fafi/shared').RestartKind; readonly team: number; readonly taker: number | null; readonly spot: { readonly x: number; readonly y: number } } | null {
    return this.currentRestart;
  }

  /** D46 Item 6: the authoritative match phase, tracked from PHASE events — independent of the
   *  connection/render gate in view(). The rematch freeze symptom was this stuck at FULLTIME. */
  get phase(): string {
    return this.matchPhase;
  }
  /** D46 Item 6: live score [away, home], driven by GOAL events. A rematch resets it to [0, 0]. */
  get scores(): readonly [number, number] {
    return [this.score[0], this.score[1]];
  }
  /** D65b perf overlay: the interpolation delay actually in use (ms) — the felt-lag lever. */
  get interpBackMs(): number {
    return this.interp.interpBackMs;
  }
  /** D65b perf overlay: how old the newest APPLIED snapshot is (ms) — stream freshness. */
  snapshotAgeMs(nowMs: number): number {
    return this.lastSnapAtMs < 0 ? -1 : nowMs - this.lastSnapAtMs;
  }
  /** D65b perf overlay: round-trip time estimate (ms) from the clock sync. */
  get rttMs(): number {
    return this.clock.rttMs;
  }
  /** Median prediction-error over the recent window, in metres (T-E2E-003 < 0.5 m). */
  medianPredictionErrorM(): number {
    if (this.predErr.length === 0) return 0;
    const sorted = [...this.predErr].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)]!;
  }

  // ── network pump (setInterval-driven, §13.2: NOT rAF — hidden tabs stop rAF entirely, and a
  //    backgrounded player must keep pinging + sending inputs at whatever cadence the browser allows) ──
  /** D45 Item 1: force the predictor to re-seed from the next authoritative snapshot (render NaN recovery). */
  reanchor(): void {
    this.predictor = null;
  }

  /** D45 Item 1: liveness watchdog for the join/resume path. A reload into a dead room used to wait on
   *  snapshots forever ("frozen"). Stage 0: no response in 5 s → drop the stored token + fresh JOIN.
   *  Stage 1: the JOIN fallback also silent in 5 s → fatal error screen. Progress (a snapshot or a lobby)
   *  clears it. Server-side (D45) a crashed room is now removed, so RESUME → BAD_RESUME → JOIN usually
   *  resolves this before the timer; the watchdog covers an unresponsive/slow server. */
  /**
   * D62 stream-liveness watchdog. The resume watchdog below only ever guarded the PRE-match connect
   * (it is gated on `awaitingConnect`, armed on the join path only, and self-clears the moment a lobby
   * arrives), so nothing in the client measured snapshot arrival DURING a match. A stream that died on
   * a live socket therefore rendered a perfect lie: entities pinned at the last snapshot, the HUD clock
   * counting on, no error. This measures time since the last APPLIED snapshot and, past the threshold,
   * flags the stream dead (the HUD shows it) and forces a fresh resume — re-seeding the snapshot buffer,
   * because a baseline this buffer no longer holds would otherwise make every future delta throw and be
   * swallowed forever.
   */
  private streamWatchdog(now: number): void {
    if (this.connection?.state !== 'PLAYING' || this.lastSnapAtMs < 0) return;
    const silentFor = now - this.lastSnapAtMs;
    if (silentFor <= STREAM_DEAD_MS) {
      this.streamDead = false;
      return;
    }
    if (this.streamDead) return; // already flagged; one resume per outage
    this.streamDead = true;
    this.snaps = new SnapshotBuffer(); // drop baselines the server will no longer delta against
    this.connection.forceResume();
  }

  private resumeWatchdog(now: number): void {
    if (!this.awaitingConnect) return;
    if (this.snapshotsApplied > 0 || this.players > 0) {
      this.awaitingConnect = false; // live (in-match snapshots) or safely in a lobby — not frozen
      return;
    }
    if (this.watchStart < 0) {
      this.watchStart = now;
      return;
    }
    if (now - this.watchStart <= RESUME_WATCHDOG_MS) return;
    if (this.watchStage === 0 && this.sentResume && !this.joinFallbackSent) {
      // RESUME got nothing — drop the stale token and fall back to a fresh JOIN
      this.watchStage = 1;
      this.watchStart = now;
      this.joinFallbackSent = true;
      try {
        sessionStorage.removeItem('fafi.resume');
      } catch {
        /* storage unavailable — nothing to clear */
      }
      const code = this.opts.join.kind === 'join' ? this.opts.join.code : this.code;
      this.mux.send(encodeJsonMsg({ t: 'JOIN_ROOM', code }));
      return;
    }
    // no stored-token resume (or the JOIN fallback also went silent) ⇒ give up cleanly
    this.watchStage = 2;
    this.awaitingConnect = false;
    this.opts.onFatal?.('Could not reach the match — the room may have ended. Back to the menu to play again.');
  }

  pump(nowMs: number): void {
    this.connection?.tick(nowMs);
    this.resumeWatchdog(nowMs);
    this.streamWatchdog(nowMs); // D62: a dead stream on a live socket must be caught, not rendered
    if (nowMs - this.lastPingMs >= PING_INTERVAL_MS) {
      this.lastPingMs = nowMs;
      this.mux.send(encodePing({ clientTimeMs: nowMs }));
    }
    if (this.predictor && this.cfg && (this.connection?.inputsEnabled ?? false)) {
      const target = this.lead.stampTick(this.clock.currentServerTick(nowMs));
      if (target - this.lastStamped > RE_ANCHOR_GAP_TICKS) {
        // clock re-anchor / long background gap: never step-simulate the chasm — drop the predictor and
        // let the next snapshot re-seed it at the authoritative tick (§10.2 resume rule; guards the
        // unbounded-advanceTo failure mode for good)
        this.predictor = null;
        return;
      }
      if (target > this.lastStamped) {
        const from = Math.max(this.lastStamped + 1, target - (MAX_CATCHUP_TICKS - 1));
        // D94: the ×3 redundancy window is sent as (firstTick + N contiguous inputs) — the wire cannot
        // express a hole (messages.ts: "for ticks firstTick..firstTick+len-1"; the server rebuilds with
        // `frame.firstTick + i`). When the catch-up clamp above SKIPS ticks, the ring still holds the
        // pre-gap entries, so the first window straddles the chasm and every input in it is re-numbered
        // to the old run: after a throttled-tab gap 103→183 the tick-183 input was transmitted as tick
        // 104 and silently dropped server-side as stale. Drop the stale tail so a frame is always one
        // contiguous run.
        if (from > this.lastStamped + 1) this.ring.length = 0;
        for (let t = from; t <= target; t++) {
          let input = this.opts.sampleInput();
          // D36 keeper-switch safety (mirror of the server-side mask — identical inputs, no mispredict):
          // freshly in control of the keeper, movement toward the own goal is stripped for ~1 s.
          if (t < this.gkMaskUntilTick && input.moveX * this.gkMaskSignX > 0) input = { ...input, moveX: 0 };
          // D53: one-touch intent — a kick tapped BEFORE possession stays armed on the wire; the sim
          // volleys the ball on the instant possession arrives (fresh-claim + held bit rule).
          // D155: `airborne` tells the buffer the SIM owns the SHOOT arm for this ball, so it must not
          // re-emit the bit — otherwise the server sees no rising edge and PT4-P11's second-press cancel
          // is swallowed for the whole 18-tick window. It defaulted to false and was never passed, so
          // D135's client half never shipped at all.
          input = { ...input, buttons: this.kickIntent.apply(input.buttons, this.lastBallCarried, t, this.lastBallAirborne) };
          // kick-edge glue detach (D17, completes D13): the frame your kick FIRES while carrying, the
          // rendered ball leaves your foot immediately instead of sticking for ~RTT. D51: PASS/THROUGH
          // fire on the PRESS edge since D49 (instant passes) — detach there; SHOOT still fires on
          // release.
          const passPressed = (input.buttons & ~this.prevSampledButtons) & (BTN.PASS | BTN.THROUGH);
          const shootReleased = (this.prevSampledButtons & BTN.SHOOT) !== 0 && (input.buttons & BTN.SHOOT) === 0;
          if (passPressed !== 0 && this.lastBallCarried) {
            // D52: the pass INPUT aims straight AT the ringed mate — the ring (client) and the receiver
            // (server, same pickPassTarget) can then only disagree if he outright teleports mid-flight.
            const yaw = this.ringAimYaw(input.aimYaw);
            if (yaw !== null) input = { ...input, aimYaw: yaw };
          }
          if ((passPressed !== 0 || shootReleased) && this.lastBallCarried) {
            this.kickDetachAtMs = nowMs;
            this.ballOwnUntilMs = nowMs + 900; // the flight renders on the present timeline (D51)
          }
          this.prevSampledButtons = input.buttons;
          this.predictor.recordInput(t, input);
          this.ring.push({ t, input });
          if (this.ring.length > 8) this.ring.shift();
          const window = this.ring.slice(Math.max(0, this.ring.length - 3)); // ×3 redundancy (§10.6)
          this.mux.send(
            encodeInputFrame({
              firstTick: window[0]!.t,
              inputs: window.map((r) => r.input),
              lastSnapTick: this.snaps.newestTick >= 0 ? this.snaps.newestTick : FULL_SENTINEL,
            }),
          );
        }
        this.lastStamped = target;
      }
      this.predictor.advanceTo(this.lastStamped, this.cfg);
    }
  }

  // ── render drive (rAF): advance the interpolation timeline + assemble the ViewState ──
  /** D117: `nowMs` is the frame's timestamp — pass the SAME value the frame's `pump()` got. view() used
   *  to call `this.now()` itself, so the sub-tick offset was measured a few hundred µs after the tick
   *  the pump had stamped; a frame landing on a tick boundary then rendered a whole tick ahead and the
   *  next one barely moved. One clock per frame removes that class of glitch entirely. */
  render(frameDeltaMs: number, nowMs?: number): ViewState | null {
    this.interp.advance(frameDeltaMs);
    this.lastFrameDeltaMs = frameDeltaMs;
    return this.view(nowMs ?? this.now());
  }
  private lastFrameDeltaMs = 16.7;
  private ballPrev: { x: number; y: number; z: number } | null = null;
  private ballBlend: { from: { x: number; y: number; z: number }; t: number } | null = null;
  private lastBallCarried = false;
  /** D155 — is the ball one the sim's aerial arm owns (free, at or above the volley band)? Latched off
   *  the newest snapshot alongside `lastBallCarried`, and read by the D53 input buffer. */
  private lastBallAirborne = false;
  private lastBallPossessor = -1;
  private prevSampledButtons = 0;
  private kickDetachAtMs = -1; // > 0 while a local kick release overrides the carried glue (D17)
  private ballOwnUntilMs = -1; // D51: while recent, a LOOSE ball still renders on the present timeline (knock-ons, my kicks)
  private readonly kickIntent = new KickIntent(); // D53: one-touch intent buffer

  /** D52: yaw from my predicted position to the mate the pass soft-lock would pick right now (the same
   *  selection the ring shows). Computed over the NEWEST snapshot so the server's own pickPassTarget —
   *  fed this aim — resolves to the same mate. Null when no teammate exists. */
  private ringAimYaw(rawAim: number): number | null {
    const newest = this.snaps.newest();
    if (!newest || !this.cfg || !this.mode) return null;
    const n = this.mode.teamSize;
    const shim = {
      players: newest.players.map((ps) => {
        const pv = dequantPlayer(ps);
        return { id: ps.id, team: ps.id < n ? 0 : 1, sentOff: flagBit(pv.flags, 1), pos: pv.pos, vel: pv.vel, calling: flagBit(pv.flags, 64) };
      }),
    } as unknown as Parameters<typeof pickPassTarget>[0];
    const bp = dequantBall(newest.ball).pos;
    const t = pickPassTarget(shim, this.ownId as never, { x: bp.x, y: bp.y }, rawAim, this.cfg.actions.kick.MAX.PASS, this.cfg);
    if (!t) return null;
    const tp = shim.players[t.id]!;
    const me = this.predictor ? this.predictor.renderedPos() : shim.players[this.ownId]?.pos;
    if (!me) return null;
    return Math.atan2(tp.pos.y - me.y, tp.pos.x - me.x);
  }

  /** Carried-glue gate (D17): authoritative possession, minus the local-kick-release override window. */
  private carriedNow(authCarried: boolean, nowMs: number): boolean {
    if (!authCarried) {
      this.kickDetachAtMs = -1; // server confirmed the release — override done
      return false;
    }
    if (this.kickDetachAtMs >= 0) {
      if (nowMs - this.kickDetachAtMs < KICK_DETACH_MAX_MS) return false; // ball is in flight locally
      this.kickDetachAtMs = -1; // server never released (kick out of reach) — restore the glue
    }
    return true;
  }

  /** Own-ball render prediction (§10.2, carried subset): while the AUTHORITATIVE possessor is the own
   *  entity, the ball rides the PREDICTED carrier's carry point (pos + facing·CARRY_POINT) — the same
   *  present-timeline the body renders on, so dribbling never trails. Everyone else's ball stays on the
   *  interpolated timeline. Timeline switches (claim/release, ~RTT late by design) blend over
   *  BALL_BLEND_MS — never a teleport. Full §10.2 (optimistic claim, kick-edge release) lands with kick
   *  bindings in M3; see D12. */
  private ballRenderPos(interpPos: { x: number; y: number; z: number }, carried: boolean, possessor: number, alpha: number): { x: number; y: number; z: number } {
    let target = interpPos;
    if (carried && this.predictor && this.cfg) {
      // D117: the SAME sub-tick sample the body renders at — otherwise a dribbled ball would step at
      // 30 Hz while the carrier it is glued to moved with the frame.
      const s = this.predictor.sample(alpha);
      const off = this.cfg.possession.CARRY_POINT;
      target = { x: s.pos.x + Math.cos(s.facing) * off, y: s.pos.y + Math.sin(s.facing) * off, z: interpPos.z };
    }
    // D44: blend the render over a timeline switch — carried↔interpolated AND any possessor change
    // (A→B on a tackle/pass). The sim now seats the transferred ball at a short gap; the client glides
    // the last ~150 ms so a change of hands never pops.
    if (carried !== this.lastBallCarried || possessor !== this.lastBallPossessor) {
      this.lastBallCarried = carried;
      this.lastBallPossessor = possessor;
      if (this.ballPrev) this.ballBlend = { from: { ...this.ballPrev }, t: 0 };
    }
    let out = target;
    if (this.ballBlend) {
      this.ballBlend.t += this.lastFrameDeltaMs / BALL_BLEND_MS;
      const t = this.ballBlend.t;
      if (t >= 1) this.ballBlend = null;
      else {
        const e = t * t * (3 - 2 * t); // smoothstep
        const f = this.ballBlend.from;
        out = { x: f.x + (target.x - f.x) * e, y: f.y + (target.y - f.y) * e, z: f.z + (target.z - f.z) * e };
      }
    }
    this.ballPrev = out;
    return out;
  }

  /**
   * D117 — how far through the current sim tick this render frame sits, 0..1. Derived from the SAME
   * clock + lead the input pump stamps with, so alpha 0 is exactly the tick the predictor last advanced
   * to and alpha grows to 1 as the next tick approaches. Clamped, so a stalled pump holds the render one
   * tick ahead at most rather than letting it run away.
   */
  private renderAlpha(nowMs: number): number {
    if (!this.predictor || this.lastStamped < 0) return 0;
    const a = this.clock.currentServerTickFloat(nowMs) + this.lead.currentLead - this.lastStamped;
    return a <= 0 ? 0 : a >= 1 ? 1 : a;
  }

  // ── ViewState assembly (§13.5): predictor for the own entity, interpolator for the rest ──
  private view(nowMs: number): ViewState | null {
    const newest = this.snaps.newest();
    if (!newest || !this.mode) return null;
    const alpha = this.renderAlpha(nowMs); // D117: sub-tick offset for the predicted own entity
    const n = this.mode.teamSize;
    const entities: ViewEntity[] = newest.players.map((ps) => {
      const pv = dequantPlayer(ps);
      const own = ps.id === this.ownId && this.predictor !== null;
      let pos = { x: pv.pos.x, y: pv.pos.y, z: pv.z };
      let vel = { x: pv.vel.x, y: pv.vel.y };
      let facing = pv.facing;
      let action = pv.action;
      let actionTicks = pv.actionTicks;
      let moveSpeed = Math.hypot(pv.vel.x, pv.vel.y);
      if (own) {
        const p = this.predictor!;
        const s = p.sample(alpha); // D117: rendered sub-tick, not the raw 30 Hz predicted state
        const st = p.state();
        pos = { x: s.pos.x, y: s.pos.y, z: st.z };
        vel = { x: st.vel.x, y: st.vel.y };
        facing = s.facing;
        action = st.action;
        actionTicks = st.actionTicks;
        moveSpeed = Math.hypot(st.vel.x, st.vel.y);
      } else {
        const r = this.interp.entity(ps.id);
        if (r) {
          pos = { x: r.pos.x, y: r.pos.y, z: r.z };
          facing = r.facing;
          // D126: take the animation state from the SAME bracket the body is rendering out of. Reading
          // it off `newest` (50–100 ms ahead of the interpolated pose) started every remote one-shot
          // before the motion it belongs to — the keeper's dive clip ran while he still stood still.
          action = r.action;
          actionTicks = r.actionTicks;
          moveSpeed = r.moveSpeed;
        }
      }
      return {
        id: ps.id,
        team: (ps.id < n ? 0 : 1) as ViewEntity['team'],
        pos,
        vel,
        facing,
        action,
        actionTicks,
        moveSpeed,
        isControlled: ps.id === this.ownId,
        isSwitchCandidate: false,
        isGk: flagBit(pv.flags, 2),
        card: cardOf(pv.flags),
        sentOff: flagBit(pv.flags, 1),
        suspendSecs: ps.suspendUntil > 0 ? Math.max(0, (ps.suspendUntil - newest.tick) / 30) : 0, // D65c return countdown
        hasBall: flagBit(pv.flags, 8),
        shouting: flagBit(pv.flags, 4),
        calling: flagBit(pv.flags, 64), // D42
        speaking: false,
      };
    });
    const ib = this.interp.ball();
    const bv = dequantBall(newest.ball);
    const ownState = this.predictor?.state();
    const ownEntity = entities.find((e) => e.isControlled) ?? null; // D137: for the keeper-penalty context
    const authCarried = newest.ball.possessor !== NO_POSSESSOR && newest.ball.possessor === this.ownId;
    const carried = this.carriedNow(authCarried, nowMs); // minus the local kick-release override (D17)
    // D51: a ball I carried MOMENTS ago (sprint knock-on, my own pass in flight) must not fall back onto
    // the interpolated timeline (~RTT+delay in the PAST — at sprint pace it rendered 1–3 m BEHIND the
    // predicted body: "the ball oscillates back", "glitches behind me before the pass"). Extrapolate the
    // NEWEST snapshot ball to the predicted present instead; strangers' balls keep the smooth interp.
    if (authCarried) this.ballOwnUntilMs = nowMs + 900;
    // D62: CLAMP the present-timeline offset. lastStamped advances off the local clock while
    // newest.tick freezes for the whole of a stall (the server's SKIP_BYTES path keeps the event
    // channel flowing, so the client stays PLAYING and keeps stamping), and an unclamped lagSec
    // rendered the ball tens of metres off the pitch before popping back.
    const rawLagSec = newest.tick >= 0 && this.lastStamped > newest.tick ? (this.lastStamped - newest.tick) / 30 : 0;
    const lagSec = Math.min(rawLagSec, BALL_EXTRAP_MAX_SEC);
    const present =
      !carried && nowMs < this.ballOwnUntilMs
        ? { x: bv.pos.x + bv.vel.x * lagSec, y: bv.pos.y + bv.vel.y * lagSec, z: bv.pos.z }
        : ib
          ? ib.pos
          : bv.pos;
    return {
      entities,
      // D110: pos is the RENDER position (interpolated/extrapolated for smoothness), but vel + possessor
      // come straight off the authoritative snapshot — the landing ring must re-fly the server's own
      // flight, not a blend of two of them.
      ball: {
        pos: this.ballRenderPos(present, carried, newest.ball.possessor, alpha),
        vel: { x: bv.vel.x, y: bv.vel.y, z: bv.vel.z },
        possessor: newest.ball.possessor === NO_POSSESSOR ? null : newest.ball.possessor,
      },
      ref: {
        pos: {
          x: dequantU16(newest.ref.posX, QUANT.POS_X.min, QUANT.POS_X.max),
          y: dequantU16(newest.ref.posY, QUANT.POS_Y.min, QUANT.POS_Y.max),
        },
        viewDir: dequantFacing(newest.ref.viewDir),
      },
      hud: {
        score: [this.score[0], this.score[1]], // driven by GOAL events (§6.2)
        clockDisplay: this.clockDisplay(),
        stamina: ownState?.stamina ?? 1,
        charge01: ownState?.charge ? Math.min(ownState.charge.ticks, this.cfg?.actions.kick.CHARGE_CAP ?? 18) / (this.cfg?.actions.kick.CHARGE_CAP ?? 18) : 0,
        chargeButton: ownState?.charge?.button ?? 0,
        chargeTicks: ownState?.charge?.ticks ?? 0,
        phase: this.connection?.state === 'PLAYING' ? this.matchPhase : (this.connection?.state ?? 'CONNECTING'), // D41: the MATCH phase drives the scoreboard
        // D137 (PT4-P21): the one context in which the client emits the keeper-dive bits.
        defendingPenalty: this.currentRestart?.kind === 'PENALTY' && ownEntity !== null && ownEntity.isGk && ownEntity.team !== this.currentRestart.team,
        aerialArmed: ((ownState?.intentBits ?? 0) & 8) !== 0, // D135 — BTN.SHOOT, off the predicted state
      },
      passTarget: this.passTargetNow(entities, ownState?.charge?.ticks ?? 0),
      attackDir0: newest.attackDir0, // D65d: team-colored goals recolor on the half-time flip
      conditions: this.mode.conditions, // D110: the landing ring flies the same condition-scaled §5.3 model as the server
    };
  }

  /** D34 receiver highlight: the teammate a PASS would soft-lock at the current aim (presentation). */
  private passTargetNow(entities: ViewEntity[], chargeTicks: number): EntityId | null {
    if (!this.cfg) return null;
    const aimYaw = this.opts.sampleInput().aimYaw; // what the next input will carry
    const shim: ViewState = {
      entities,
      ball: { pos: this.interp.ball()?.pos ?? { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, possessor: null },
      ref: { pos: { x: 0, y: 0 }, viewDir: 0 },
      hud: { score: [0, 0], clockDisplay: '', stamina: 1, charge01: 0, chargeButton: 0, chargeTicks: 0, phase: '', defendingPenalty: false, aerialArmed: false },
      passTarget: null,
      attackDir0: 1,
      conditions: 'DRY',
    };
    return passSoftLock(shim, this.ownId, aimYaw, chargeTicks, this.cfg);
  }

  private clockDisplay(): string {
    // D62: the AUTHORITATIVE match clock (snapshot header). It resets at half-time, pauses during
    // stoppages, and — critically — stops when the stream stops, instead of counting merrily over a
    // frozen pitch and hiding a dead connection.
    const ticks = Math.max(0, this.clockTicks);
    const secs = Math.floor(ticks / 30);
    return `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
  }

  get connState(): ConnState | 'LOBBY' {
    return this.connection?.state ?? 'LOBBY';
  }
  get roomCode(): string {
    return this.code;
  }

  /** Lobby snapshot for the pre-match UI (§13). */
  lobby(): { code: string; players: number; teamSize: number; teams: [number, number]; voice: boolean; difficulty: number; isHost: boolean; yourTeam: number; started: boolean } {
    return { code: this.code, players: this.players, teamSize: this.teamSize, teams: [...this.teams] as [number, number], voice: this.voice, difficulty: this.difficulty, isHost: this.isHost, yourTeam: this.yourTeam, started: this.matchStarted || this.connection !== null };
  }
  /** True once the match has kicked off (leave the lobby screen). */
  inMatch(): boolean {
    return this.connection !== null || this.ownId >= 0;
  }
  /** Host: begin the match (solo-start allowed — bots fill the rest). */
  requestStart(): void {
    if (this.isHost && this.code && !this.startSent) {
      this.startSent = true; // debounce repeated clicks (server also dedupes on room.started)
      this.mux.send(encodeJsonMsg({ t: 'START' }));
    }
  }
  /** Pick a side in the lobby. */
  requestTeam(team: 0 | 1): void {
    this.mux.send(encodeJsonMsg({ t: 'SET_TEAM', team }));
  }
  /** Host: change the roster size in the lobby. */
  requestMode(teamSize: number): void {
    if (this.isHost) this.mux.send(encodeJsonMsg({ t: 'SET_MODE', teamSize }));
  }
  /** Host: toggle proximity voice for the lobby (P14). */
  requestVoice(on: boolean): void {
    if (this.isHost) this.mux.send(encodeJsonMsg({ t: 'SET_MODE', voice: on }));
  }
  /** D46 Items 3+4: host changes the AI difficulty (1..5) — the server clamps + re-broadcasts LOBBY_STATE. */
  requestDifficulty(level: number): void {
    if (this.isHost) this.mux.send(encodeJsonMsg({ t: 'SET_MODE', difficulty: level }));
  }

  /** D18: manual cycle-switch (Tab) — the router picks the next entity server-side. */
  /** D43: the wire-delivered sim config (trajectory preview must match the server's launch math). */
  get simCfg(): SimConfig | null {
    return this.cfg;
  }

  /** D41: vote YES for a rematch — everyone who presses it lands in this room's next match. */
  voteRematch(): void {
    this.mux.send(encodeJsonMsg({ t: 'REMATCH_VOTE', vote: true }));
  }

  requestCycle(): void {
    this.mux.send(encodeSwitchRequest({ target: SWITCH_CYCLE }));
  }

  /** One-line HUD status: room · state · net stats (the M2 gate's eyeball instruments). */
  status(): string {
    const rtt = Math.round(this.clock.rttMs);
    const conn = this.connection ? this.connection.state : this.players >= 2 ? 'STARTING' : 'WAITING FOR OPPONENT';
    const err = this.lastError ? ` · ⚠ ${this.lastError}` : '';
    const last = this.ticker.entries().at(-1);
    const feed = last ? ` · t${last.tick} ${last.event.t}` : '';
    return `room ${this.code || '…'} · ${conn} · rtt ${rtt} ms · lead ${this.lead.currentLead}t · interp ${Math.round(this.interp.interpBackMs)} ms · ${this.score[0]}-${this.score[1]}${feed}${err}`;
  }
}
